import uuid from "uuid-random";
import { useStore } from "zustand";
import { createStore, StoreApi } from "zustand/vanilla";
import type {
  SessionAssignationContext,
  SessionAssignationFilters,
  SessionAssignationIssue,
  SessionAssignationOriginal,
  SessionAssignationPhase,
  SessionAssignationPolicy,
  SessionAssignationRequestToken,
  SessionAssignationRow,
  SessionAssignationRowPatch,
  SessionAssignationRowValues,
  SessionAssignationSeed,
  SessionAssignationValidated,
} from "@services/types/session-assignation/sessionAssignationTypes";
import {
  adaptScheduleInstances,
  intervalForDate,
  normalizeRowPatch,
  sameRowValues,
  SessionAssignationError,
} from "@services/collections/session-assignation/adapters/occurrences";
import {
  canEditAssignation,
  canCreateOccurrence,
  canDeleteOccurrence,
  canPatchOccurrence,
  isPastOccurrence,
} from "@services/collections/session-assignation/utils/permissions";
import { freezeSnapshot } from "@services/collections/session-assignation/utils/immutable";
import type { Block, Interval, Section } from "@models/ISchema";

const EMPTY_POLICY: SessionAssignationPolicy = {
  permissions: {},
  abilities: {},
  courseCanEdit: false,
  isAssignationLocked: false,
  thresholdDate: null,
};

export const sessionAssignationContextKey = (context: SessionAssignationContext): string =>
  JSON.stringify([context.scenarioId, context.originId, context.linkId]);

export interface SessionAssignationState {
  context: SessionAssignationContext | null;
  generation: number;
  revision: number;
  requestId: number;
  phase: SessionAssignationPhase;
  sections: readonly Section[];
  weeks: readonly Interval[];
  blocks: readonly Block[];
  originals: readonly SessionAssignationOriginal[];
  originalRows: readonly SessionAssignationRow[];
  rows: readonly SessionAssignationRow[];
  policy: SessionAssignationPolicy;
  filters: SessionAssignationFilters;
  editorDirty: boolean;
  issues: readonly SessionAssignationIssue[];
  validated: SessionAssignationValidated | null;
  reset: () => void;
  beginLoad: (context: SessionAssignationContext) => number | null;
  failLoad: (generation: number) => void;
  initialize: (seed: SessionAssignationSeed, generation?: number) => boolean;
  setPolicy: (policy: SessionAssignationPolicy) => void;
  setFilters: (filters: Partial<SessionAssignationFilters>) => void;
  setEditorDirty: (dirty: boolean) => void;
  addRow: (values: SessionAssignationRowValues) => string | null;
  updateRow: (localId: string, patch: SessionAssignationRowPatch) => boolean;
  deleteRows: (localIds: readonly string[]) => boolean;
  undoDeletion: (localId: string) => boolean;
  restoreRows: (localIds: readonly string[]) => boolean;
  duplicateRows: (localIds: readonly string[]) => readonly string[] | null;
  beginValidation: () => SessionAssignationRequestToken | null;
  isCurrent: (token: SessionAssignationRequestToken) => boolean;
  failValidation: (
    token: SessionAssignationRequestToken,
    issues: readonly SessionAssignationIssue[],
  ) => void;
  acceptValidation: (validated: SessionAssignationValidated) => boolean;
  backToEditing: () => void;
  beginCommit: () => SessionAssignationValidated | null;
  failCommit: (token: SessionAssignationRequestToken, issue: SessionAssignationIssue) => void;
  completeCommit: (token: SessionAssignationRequestToken) => boolean;
}

const initialData = () => ({
  context: null,
  generation: 0,
  revision: 0,
  requestId: 0,
  phase: "idle" as SessionAssignationPhase,
  sections: [],
  weeks: [],
  blocks: [],
  originals: [],
  originalRows: [],
  rows: [],
  policy: EMPTY_POLICY,
  filters: { sectionId: null, fromDate: null, toDate: null },
  editorDirty: false,
  issues: [],
  validated: null,
});

export const hasSessionAssignationChanges = (
  state: Pick<SessionAssignationState, "editorDirty" | "rows">,
): boolean => state.editorDirty || state.rows.some(row => row.pendingState !== "unchanged");

const editingInvalidation = (state: SessionAssignationState) => ({
  revision: state.revision + 1,
  phase: "editing" as const,
  validated: null,
  issues: [],
});

const isProtected = (row: SessionAssignationRowValues, state: SessionAssignationState): boolean => {
  const week = state.weeks.find(item => item.id === row.intervalId);
  return !week || week.isInTerm === false || isPastOccurrence(row, state.policy, week.startingDate);
};

const withPendingState = (
  row: SessionAssignationRow,
  values: SessionAssignationRowValues,
  originals: readonly SessionAssignationRow[],
): SessionAssignationRow => {
  if (!row.origin) return { ...row, ...values, origin: null, pendingState: "new" };
  const original = originals.find(item => item.localId === row.localId);
  return {
    ...row,
    ...values,
    origin: row.origin,
    pendingState: original && sameRowValues(original, values) ? "unchanged" : "updated",
  };
};

export const createSessionAssignationStore = (): StoreApi<SessionAssignationState> =>
  createStore<SessionAssignationState>((set, get) => ({
    ...initialData(),
    reset: () => set(state => ({ ...initialData(), generation: state.generation + 1 })),
    beginLoad: context => {
      const state = get();
      const sameContext =
        state.context &&
        sessionAssignationContextKey(state.context) === sessionAssignationContextKey(context);
      if (state.phase === "committing" || (sameContext && hasSessionAssignationChanges(state)))
        return null;
      const generation = state.generation + 1;
      set({
        ...initialData(),
        context: freezeSnapshot(context),
        generation,
        phase: "loading",
        policy: state.policy,
      });
      return generation;
    },
    failLoad: generation => {
      if (get().generation === generation)
        set({ phase: "load-error", issues: [{ code: "LOAD_FAILED" }] });
    },
    initialize: (seed, generation) => {
      const state = get();
      if (
        generation !== undefined &&
        (state.generation !== generation || state.phase !== "loading")
      )
        return false;
      if (state.phase === "committing" || hasSessionAssignationChanges(state)) return false;
      const rows = adaptScheduleInstances(seed.instances);
      const sectionIds = new Set(seed.sections.map(section => section.id));
      if (rows.some(row => !sectionIds.has(row.sectionId)))
        throw new SessionAssignationError("INCONSISTENT_LOAD");
      const originals: SessionAssignationOriginal[] = seed.sections.flatMap(section => {
        const byId = new Map(
          [...section.sessions, ...(section.unasignedSessions ?? [])]
            .filter(Boolean)
            .map(session => [session.id, session]),
        );
        return [...byId.values()].map(session => ({ sectionId: section.id, session }));
      });
      const originalIds = new Set(originals.map(original => original.session.id));
      if (rows.some(row => !originalIds.has(row.origin.sessionId)))
        throw new SessionAssignationError("INCONSISTENT_LOAD");
      const snapshot = freezeSnapshot({
        context: seed.context,
        sections: seed.sections,
        weeks: seed.weeks,
        blocks: seed.blocks,
        originals,
        originalRows: rows,
        rows,
      });
      set({
        ...initialData(),
        ...snapshot,
        policy: state.policy,
        generation: generation ?? state.generation + 1,
        phase: "editing",
      });
      return true;
    },
    setPolicy: policy => {
      const state = get();
      if (JSON.stringify(state.policy) === JSON.stringify(policy)) return;
      const shouldInvalidate = ["editing", "validating", "results"].includes(state.phase);
      set({
        policy: freezeSnapshot(policy),
        ...(shouldInvalidate ? editingInvalidation(state) : {}),
      });
    },
    setFilters: filters => set(state => ({ filters: { ...state.filters, ...filters } })),
    setEditorDirty: editorDirty => {
      const current = get();
      if (
        !["editing", "validating", "results"].includes(current.phase) ||
        current.editorDirty === editorDirty
      )
        return;
      set(state => ({ editorDirty, ...editingInvalidation(state) }));
    },
    addRow: values => {
      const state = get();
      if (
        state.phase === "committing" ||
        !canCreateOccurrence(state.policy) ||
        !state.sections.some(section => section.id === values.sectionId)
      )
        return null;
      const interval = intervalForDate(values.date, state.weeks);
      const next = normalizeRowPatch(
        { ...values, intervalId: interval.id },
        { scheduleMode: values.scheduleMode },
        state.weeks,
        state.blocks,
      );
      if (isProtected(next, state)) return null;
      if (
        (next.classrooms.length && !state.policy.abilities.can_assign_classrooms) ||
        (next.instructors.length && !state.policy.abilities.can_assign_instructors)
      )
        return null;
      const row: SessionAssignationRow = {
        ...next,
        localId: uuid(),
        origin: null,
        pendingState: "new",
      };
      set({ rows: freezeSnapshot([...state.rows, row]), ...editingInvalidation(state) });
      return row.localId;
    },
    updateRow: (localId, patch) => {
      const state = get();
      const row = state.rows.find(item => item.localId === localId);
      const original = state.originalRows.find(item => item.localId === localId);
      if (
        state.phase === "committing" ||
        !row ||
        row.pendingState === "deleted" ||
        isProtected(original ?? row, state)
      )
        return false;
      const next = normalizeRowPatch(row, patch, state.weeks, state.blocks);
      if (
        !state.sections.some(section => section.id === next.sectionId) ||
        isProtected(next, state) ||
        !canPatchOccurrence(row, next, state.policy)
      )
        return false;
      const updated = withPendingState(row, next, state.originalRows);
      set({
        rows: freezeSnapshot(state.rows.map(item => (item.localId === localId ? updated : item))),
        ...editingInvalidation(state),
      });
      return true;
    },
    deleteRows: localIds => {
      const state = get();
      const ids = new Set(localIds);
      const selected = state.rows.filter(row => ids.has(row.localId));
      if (
        state.phase === "committing" ||
        !canDeleteOccurrence(state.policy) ||
        selected.length !== ids.size ||
        selected.some(row =>
          isProtected(
            state.originalRows.find(original => original.localId === row.localId) ?? row,
            state,
          ),
        )
      )
        return false;
      const rows = state.rows.flatMap<SessionAssignationRow>(row => {
        if (!ids.has(row.localId)) return [row];
        if (!row.origin) return [];
        return [{ ...row, origin: row.origin, pendingState: "deleted" }];
      });
      set({ rows: freezeSnapshot(rows), ...editingInvalidation(state) });
      return true;
    },
    undoDeletion: localId => {
      const state = get();
      const row = state.rows.find(item => item.localId === localId);
      if (
        state.phase === "committing" ||
        !row ||
        row.pendingState !== "deleted" ||
        !canDeleteOccurrence(state.policy) ||
        isProtected(state.originalRows.find(original => original.localId === localId) ?? row, state)
      )
        return false;
      const restored = withPendingState(row, row, state.originalRows);
      set({
        rows: freezeSnapshot(state.rows.map(item => (item.localId === localId ? restored : item))),
        ...editingInvalidation(state),
      });
      return true;
    },
    restoreRows: localIds => {
      const state = get();
      const ids = new Set(localIds);
      const selected = state.rows.filter(row => ids.has(row.localId));
      if (
        state.phase === "committing" ||
        !canEditAssignation(state.policy) ||
        selected.length !== ids.size ||
        !selected.some(row => row.pendingState !== "unchanged")
      )
        return false;
      for (const row of selected) {
        const original = state.originalRows.find(item => item.localId === row.localId);
        if (!original) {
          if (!canCreateOccurrence(state.policy)) return false;
        } else if (
          isProtected(original, state) ||
          (row.pendingState === "deleted"
            ? !canDeleteOccurrence(state.policy)
            : !canPatchOccurrence(row, original, state.policy))
        )
          return false;
      }
      set({
        rows: freezeSnapshot(
          state.rows.flatMap<SessionAssignationRow>(row => {
            if (!ids.has(row.localId)) return [row];
            const original = state.originalRows.find(item => item.localId === row.localId);
            return original ? [original] : [];
          }),
        ),
        ...editingInvalidation(state),
      });
      return true;
    },
    duplicateRows: localIds => {
      const state = get();
      const ids = new Set(localIds);
      const selected = state.rows.filter(row => ids.has(row.localId));
      if (
        state.phase !== "editing" ||
        !canCreateOccurrence(state.policy) ||
        !ids.size ||
        selected.length !== ids.size ||
        selected.some(
          row =>
            row.pendingState === "deleted" ||
            isProtected(
              state.originalRows.find(original => original.localId === row.localId) ?? row,
              state,
            ),
        )
      )
        return null;
      const copies: SessionAssignationRow[] = [];
      for (const row of selected) {
        if (
          (row.classrooms.length && !state.policy.abilities.can_assign_classrooms) ||
          (row.instructors.length && !state.policy.abilities.can_assign_instructors)
        )
          return null;
        const values = normalizeRowPatch(
          row,
          { scheduleMode: row.scheduleMode },
          state.weeks,
          state.blocks,
        );
        copies.push({ ...values, localId: uuid(), origin: null, pendingState: "new" });
      }
      set({ rows: freezeSnapshot([...state.rows, ...copies]), ...editingInvalidation(state) });
      return copies.map(row => row.localId);
    },
    beginValidation: () => {
      const state = get();
      if (
        state.phase !== "editing" ||
        state.editorDirty ||
        !state.context ||
        !canEditAssignation(state.policy) ||
        !hasSessionAssignationChanges(state)
      )
        return null;
      const requestId = state.requestId + 1;
      set({ phase: "validating", requestId, validated: null, issues: [] });
      return { generation: state.generation, revision: state.revision, requestId };
    },
    isCurrent: token => {
      const state = get();
      return (
        state.generation === token.generation &&
        state.revision === token.revision &&
        state.requestId === token.requestId
      );
    },
    failValidation: (token, issues) => {
      if (get().isCurrent(token))
        set({ phase: "editing", validated: null, issues: freezeSnapshot(issues) });
    },
    acceptValidation: validated => {
      if (!get().isCurrent(validated.token) || get().phase !== "validating") return false;
      set({ phase: "results", validated: freezeSnapshot(validated), issues: [] });
      return true;
    },
    backToEditing: () => {
      if (get().phase !== "committing") set(state => editingInvalidation(state));
    },
    beginCommit: () => {
      const state = get();
      if (
        state.phase !== "results" ||
        !state.validated ||
        !state.isCurrent(state.validated.token) ||
        state.validated.result.userCanSkipValidations !== true
      )
        return null;
      set({ phase: "committing", issues: [] });
      return state.validated;
    },
    failCommit: (token, issue) => {
      if (get().isCurrent(token)) set({ phase: "results", issues: [issue] });
    },
    completeCommit: token => {
      if (!get().isCurrent(token) || get().phase !== "committing") return false;
      set({
        phase: "saved",
        rows: [],
        originalRows: [],
        originals: [],
        validated: null,
        editorDirty: false,
        issues: [],
      });
      return true;
    },
  }));

export const sessionAssignationStore = createSessionAssignationStore();
export const useSessionAssignationStore = <T>(selector: (state: SessionAssignationState) => T): T =>
  useStore(sessionAssignationStore, selector);
